import React from 'react'
import {
  PROCESS_ELAVON_PAYMENT,
  GET_ELAVON_TOKEN_BY_ITEM_GUIDS,
  CLEAR_FAILED_WEB_CART_BY_REF_NUM,
  GET_ELAVON_TOKEN_BY_REF_NUM,
  GET_WEB_CART_BY_REF_NUM
} from 'requests'
import { PaymentButton } from '../Checkout/Checkout'
import { useAppSetting } from '../../Utilities/hooks'
import PropTypes from 'prop-types'

const PASS_ITEM_GUIDS_ON_CHECKOUT = process.env.PASS_ITEM_GUIDS_ON_CHECKOUT

export const useElavon = ({ method }) => {
  const [jQueryLoaded, setJQueryLoaded] = React.useState(false)
  const [convergeLoaded, setConvergeLoaded] = React.useState(false)
  const {setting: scriptSrc} = useAppSetting('ELAVON_CONVERGE_SCRIPT_SRC')
  const jqueryScript = React.useRef()
  const convergeScript = React.useRef()

  React.useEffect(() => {
    if(scriptSrc){
      if (!window.jQuery || (window.jQuery && window.jQuery.fn.jquery === '3.4.1')) {
        jqueryScript.current = document.createElement('script')
        jqueryScript.current.type = 'text/javascript'
        jqueryScript.current.src = 'https://ajax.googleapis.com/ajax/libs/jquery/3.4.1/jquery.min.js'
        document.head.appendChild(jqueryScript.current)
        jqueryScript.current.onload = e => {
          setJQueryLoaded(true)
        }
      } else {
        setJQueryLoaded(true)
      }
      if (method === 'checkout' ? !window.ConvergeEmbeddedPayment : !window.PayWithConverge) {
        convergeScript.current = document.createElement('script')
        convergeScript.current.type = 'text/javascript'
        convergeScript.current.src = `${scriptSrc.VALUE}${
          method === 'checkout' ? 'Checkout' : 'PayWithConverge'
        }.js`
        document.head.appendChild(convergeScript.current)
        convergeScript.current.onload = e => {
          setConvergeLoaded(true)
        }
      } else {
        setConvergeLoaded(true)
      }
    }
  }, [scriptSrc])
  return jQueryLoaded && convergeLoaded
}

export const ElavonProvider = ({ method, children }) => {
  const elavonLoaded = useElavon({ method })

  return typeof children === 'function' ? children(elavonLoaded) : children
}
ElavonProvider.propTypes = {
  children: PropTypes.func.isRequired
}

export const getElavonTokenByItemGuids = (guids, riskCheckInfo) =>
  new Promise((resolve, reject) => {
    GET_ELAVON_TOKEN_BY_ITEM_GUIDS(guids, riskCheckInfo, resolve, reject)
  })

export const getElavonTokenByRefNum = refNum =>
  new Promise((resolve, reject) => {
    GET_ELAVON_TOKEN_BY_REF_NUM(refNum, resolve, reject)
  })

export const clearFailedWebCart = (refNum, errorMessage, callback) =>
  new Promise((resolve, reject) => {
    CLEAR_FAILED_WEB_CART_BY_REF_NUM(refNum, () => callback(errorMessage), reject)
  })

export const submitElavonCheckout = (values, guids, riskCheckInfo) => {
  if (PASS_ITEM_GUIDS_ON_CHECKOUT) {
    return new Promise((resolve, reject) => {
      getElavonTokenByItemGuids(guids, riskCheckInfo)
      .then(cart => {
        const {
          FIRST_NAME,
          LAST_NAME,
          COMPANY,
          CC_NUMBER,
          CC_EXPIRATION,
          CC_CVV,
          BILLING_ADDRESS,
          PHONE_NUMBER,
          PHONE_EXTENSION
        } = values

        const maskedPhone = PHONE_NUMBER.replace(/\D+/g, '');

        window.ConvergeEmbeddedPayment.pay(
          {
            ssl_txn_auth_token: cart.PAY_TOKEN,
            ssl_card_number: CC_NUMBER,
            ssl_exp_date: CC_EXPIRATION,
            ssl_first_name: FIRST_NAME,
            ssl_last_name: LAST_NAME,
            ssl_company: COMPANY,
            ssl_phone: maskedPhone,
            ssl_avs_address: BILLING_ADDRESS.ADDR1,
            ssl_address2: BILLING_ADDRESS.ADDR2,
            ssl_city: BILLING_ADDRESS.CITY,
            ssl_state: BILLING_ADDRESS.STATE,
            ssl_avs_zip: BILLING_ADDRESS.POSTAL_CODE.replace(/[^a-z0-9]/g, ''),
            ssl_cvv2cvc2: CC_CVV,
            ssl_merchant_id: cart.WEB_CART_ADDITIONAL_PROPS.ELAVON_API_MERCHANT_ID,
            ssl_user_id: cart.WEB_CART_ADDITIONAL_PROPS.ELAVON_API_USER_ID,
            ssl_pin: cart.WEB_CART_ADDITIONAL_PROPS.ELAVON_API_PIN,
            ssl_transaction_type: 'ccsale',
            ssl_description: cart.REF_NUM
          },
          {
            onError: err => clearFailedWebCart(cart.REF_NUM, err, reject),
            onDeclined: err => clearFailedWebCart(cart.REF_NUM, err, reject),
            onApproval: res => {
            PROCESS_ELAVON_PAYMENT(
              {
                ...res,
                VALUES: {
                  FIRST_NAME,
                  LAST_NAME,
                  COMPANY,
                  PHONE_NUMBER: maskedPhone,
                  BILLING_ADDRESS,
                  PHONE_EXTENSION
                }
              },
              resolve,
              reject
            )
            }
          }
        )
      }).catch(reject)
    })
  }
  else {
    let refNum = guids
    return new Promise((resolve, reject) => {
      GET_WEB_CART_BY_REF_NUM(
        refNum,
        ({
          WEB_CART_ADDITIONAL_PROPS: { ELAVON_API_MERCHANT_ID, ELAVON_API_USER_ID, ELAVON_API_PIN }
        }) => {
          getElavonTokenByRefNum(refNum)
          .then(token => {
            const {
              FIRST_NAME,
              LAST_NAME,
              COMPANY,
              CC_NUMBER,
              CC_EXPIRATION,
              CC_CVV,
              BILLING_ADDRESS,
              PHONE_NUMBER,
              PHONE_EXTENSION
            } = values
  
            const maskedPhone = PHONE_NUMBER.replace(/\D+/g, '');
  
            window.ConvergeEmbeddedPayment.pay(
              {
                ssl_txn_auth_token: token,
                ssl_card_number: CC_NUMBER,
                ssl_exp_date: CC_EXPIRATION,
                ssl_first_name: FIRST_NAME,
                ssl_last_name: LAST_NAME,
                ssl_company: COMPANY,
                ssl_phone: maskedPhone,
                ssl_avs_address: BILLING_ADDRESS.ADDR1,
                ssl_address2: BILLING_ADDRESS.ADDR2,
                ssl_city: BILLING_ADDRESS.CITY,
                ssl_state: BILLING_ADDRESS.STATE,
                ssl_avs_zip: BILLING_ADDRESS.POSTAL_CODE.replace(/[^a-z0-9]/g, ''),
                ssl_cvv2cvc2: CC_CVV,
                ssl_merchant_id: ELAVON_API_MERCHANT_ID,
                ssl_user_id: ELAVON_API_USER_ID,
                ssl_pin: ELAVON_API_PIN,
                ssl_transaction_type: 'ccsale',
                ssl_description: refNum
              },
              {
                onError: reject,
                onDeclined: reject,
                onApproval: res => {
                  PROCESS_ELAVON_PAYMENT(
                    {
                      ...res,
                      VALUES: {
                        FIRST_NAME,
                        LAST_NAME,
                        COMPANY,
                        PHONE_NUMBER: maskedPhone,
                        BILLING_ADDRESS,
                        PHONE_EXTENSION
                      }
                    },
                    resolve,
                    reject
                  )
                }
              }
            )
          })
          .catch(reject)
        },reject
      )
    })
  }
}
      
export const ElavonCheckoutSection = ({ refNum, error, onPrePaymentError, onComplete }) => {
  const elavonLoaded = useElavon()
  const [postPaymentError, setPostPaymentError] = React.useState(null)

  const startTransaction = () => {
    getElavonTokenByItemGuids(refNum).then(token => {
      var paymentFields = {
        ssl_txn_auth_token: token
      }
      var callback = {
        onError: err => onPrePaymentError,
        onCancelled: () => {},
        onDeclined: err => setPostPaymentError(err),
        onApproval: res => {
          PROCESS_ELAVON_PAYMENT(res, res => onComplete(res), err => setPostPaymentError(err))
        }
      }
      window.PayWithConverge.open(paymentFields, callback)
    })
  }

  return (
    <div className="flex-column">
      <PaymentButton error={error} onClick={startTransaction} disabled={!elavonLoaded}>
        Pay with Credit Card
      </PaymentButton>
      {postPaymentError ? <p className="payment-error">{postPaymentError}</p> : null}
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/Elavon/Elavon.js